/**
 * StudioPublicFrame.tsx — Exibição pública de páginas do Studio
 *
 * O endereço técnico da função `studio-public-page` entrega o HTML como texto
 * simples (decisão da camada de hospedagem, fora do nosso controle). Por isso
 * a página é exibida AQUI, no domínio do aplicativo: buscamos o HTML pronto na
 * função (mesma fonte única, sem recompilar nada) e o apresentamos em moldura
 * isolada (`iframe` com `sandbox`), o mesmo nível de isolamento da prévia do
 * editor.
 *
 * Rotas atendidas:
 *   - `/studio-preview/:token` — prévia assinada (15 min), sempre sem indexação;
 *   - `/lp/*`                  — página publicada (landing page), com cache/ETag
 *     e registro de acesso preservados na função de origem.
 *
 * Formulários: a página publicada aponta os formulários para `studio-page-submit`
 * (que responderia em texto). Aqui o envio é interceptado dentro da moldura e
 * feito por `fetch`, trocando o conteúdo por uma tela de agradecimento — assim
 * o visitante nunca vê código na tela.
 *
 * RELEASE: v1.0.0 - 2026-09-10 - Versão inicial (entrega pelo domínio do app).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

/** Endereço base das funções do projeto (mesma configuração do cliente oficial). */
const URL_FUNCOES = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;

/** Estados possíveis da tela pública. */
type EstadoTela =
  | { tipo: 'carregando' }
  | { tipo: 'erro'; mensagem: string }
  | { tipo: 'pronto'; html: string };

/** Tela simples de aviso (carregando/erro), no padrão visual claro do sistema. */
function TelaAviso({ titulo, detalhe }: { titulo: string; detalhe?: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-6">
      <div className="w-full max-w-sm rounded-xl bg-card p-8 text-center shadow-lg ring-1 ring-border">
        <p className="text-base font-semibold text-foreground">{titulo}</p>
        {detalhe ? <p className="mt-2 text-sm text-muted-foreground">{detalhe}</p> : null}
      </div>
    </div>
  );
}

/** HTML da tela de agradecimento exibida dentro da moldura após o envio. */
const HTML_AGRADECIMENTO = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="utf-8" />
<style>body{margin:0;min-height:100vh;display:flex;align-items:center;justify-content:center;
font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Arial,sans-serif;background:#f8fafc;color:#111827}
div{text-align:center;padding:32px}h1{font-size:22px;margin:0 0 8px}p{color:#6b7280;margin:0}</style>
</head><body><div><h1>Recebido com sucesso!</h1><p>Em breve entraremos em contato.</p></div></body></html>`;

export default function StudioPublicFrame() {
  const params = useParams();
  const location = useLocation();
  const quadroRef = useRef<HTMLIFrameElement | null>(null);
  const [estado, setEstado] = useState<EstadoTela>({ tipo: 'carregando' });

  /**
   * Endereço de origem do conteúdo na função, preservando os parâmetros da
   * campanha (utm_*, referrer etc.) para que a contagem de acesso e as
   * métricas continuem registrando a origem do visitante.
   */
  const enderecoOrigem = useMemo(() => {
    if (params.token) {
      return `${URL_FUNCOES}/studio-public-page?preview_token=${encodeURIComponent(params.token)}`;
    }
    // Caminho da landing page: tudo após `/lp/`, inclusive subpastas.
    const caminho = location.pathname.replace(/^\/lp\/?/, '').replace(/\/+$/g, '');
    if (!caminho) return null;
    const repasse = new URLSearchParams(location.search);
    repasse.set('path', caminho);
    if (!repasse.get('host')) repasse.set('host', window.location.host);
    return `${URL_FUNCOES}/studio-public-page?${repasse.toString()}`;
  }, [params.token, location.pathname, location.search]);

  // Busca o HTML pronto na função (a função responde texto; quem renderiza é a moldura).
  useEffect(() => {
    if (!enderecoOrigem) {
      setEstado({ tipo: 'erro', mensagem: 'Endereço não informado.' });
      return;
    }
    let ativo = true;
    setEstado({ tipo: 'carregando' });
    fetch(enderecoOrigem, { headers: { Accept: 'text/html' } })
      .then(async (resposta) => {
        const texto = await resposta.text();
        if (!ativo) return;
        if (!resposta.ok) {
          setEstado({
            tipo: 'erro',
            mensagem:
              resposta.status === 403
                ? 'Este link de pré-visualização expirou. Gere um novo no editor.'
                : 'Página não encontrada ou indisponível no momento.',
          });
          return;
        }
        setEstado({ tipo: 'pronto', html: texto });
      })
      .catch(() => {
        if (ativo) setEstado({ tipo: 'erro', mensagem: 'Não foi possível carregar a página.' });
      });
    return () => {
      ativo = false;
    };
  }, [enderecoOrigem]);

  /**
   * Intercepta o envio de formulários DENTRO da moldura: o destino padrão
   * responderia em texto puro; aqui enviamos por `fetch` e mostramos a tela de
   * agradecimento no lugar do conteúdo. Feito em fase de captura, antes de
   * qualquer comportamento do documento.
   */
  useEffect(() => {
    if (estado.tipo !== 'pronto') return;
    const quadro = quadroRef.current;
    const documento = quadro?.contentDocument;
    if (!quadro || !documento) return;

    const aoEnviar = async (evento: SubmitEvent) => {
      const formulario = evento.target as HTMLFormElement | null;
      if (!formulario || !formulario.action) return;
      evento.preventDefault();
      const botao = formulario.querySelector<HTMLButtonElement>('button[type="submit"], button:not([type])');
      if (botao) botao.disabled = true;
      try {
        const resposta = await fetch(formulario.action, {
          method: (formulario.method || 'post').toUpperCase(),
          body: new FormData(formulario),
        });
        if (!resposta.ok) throw new Error('falha no envio');
        // Sucesso: a moldura passa a mostrar a tela de agradecimento.
        quadro.srcdoc = HTML_AGRADECIMENTO;
      } catch {
        if (botao) botao.disabled = false;
        window.alert('Não foi possível enviar. Tente novamente em instantes.');
      }
    };

    documento.addEventListener('submit', aoEnviar, true);
    return () => documento.removeEventListener('submit', aoEnviar, true);
  }, [estado]);

  if (estado.tipo === 'carregando') {
    return <TelaAviso titulo="Carregando página..." />;
  }
  if (estado.tipo === 'erro') {
    return <TelaAviso titulo="Página indisponível" detalhe={estado.mensagem} />;
  }

  return (
    <iframe
      ref={quadroRef}
      title="Página publicada"
      // Mesmo isolamento da prévia do editor: sem scripts, sem acesso ao app.
      sandbox="allow-forms"
      srcDoc={estado.html}
      className="block h-screen w-full border-0 bg-white"
    />
  );
}
